import {
  INTERPOLATION_BUFFER_MS_DEFAULT,
  SNAPSHOT_STALL_TIMEOUT_MS,
} from '@trames/shared/constants';
import Phaser from 'phaser';

import { connectGameRoom, type GameClientHandle } from '../net/game-client.js';
import { createRoomReconciler } from '../net/reconciliation.js';
import { SnapshotBuffer } from '../net/snapshot-buffer.js';
import { lerpCameraStep, type CameraTarget } from '../render/camera.js';
import { DamageNumberLayer } from '../render/damage-numbers.js';
import { DecalLayer } from '../render/decals.js';
import { HitstopController } from '../render/hitstop.js';
import { interpolateEntities, type EntitySample } from '../render/interpolation.js';
import { DEFAULT_TILE, worldToScreen } from '../render/iso.js';
import { EntitySprite, type SpriteKind } from '../render/player-sprite.js';
import { ScreenFlash } from '../render/screen-flash.js';
import { drawIsoTilemap } from '../render/tilemap.js';
import { HudCombat } from '../ui/HudCombat.js';

import type { GameSnapshotPayload } from '@trames/shared/schemas';

/**
 * GameScene (TKT-014 + TKT-015 + TKT-037).
 *
 * Render del tilemap isométrico + cámara con lerp + jugadores y enemigos
 * interpolados a partir de los snapshots autoritativos. Buffer de 100 ms
 * (configurable runtime via `setInterpolationBufferMs`).
 *
 * Players y enemies viven en MapSchemas separados en el server y se reciben
 * en arrays distintos del snapshot (`entities` vs `enemies`). El cliente los
 * pinta con paleta visual diferente para que el jugador identifique sin
 * ambigüedad qué es amistoso.
 *
 * No simula nada: el sprite copia la posición autoritativa con retraso.
 */

export class GameScene extends Phaser.Scene {
  static readonly KEY = 'GameScene';

  private cameraTargetWorld: CameraTarget | null = null;
  private cameraPositionScreen: CameraTarget = { x: 0, y: 0 };
  /**
   * Origen en pantalla del world (0,0). Debe coincidir con el origin que usa
   * `drawIsoTilemap` para que sprites y tiles se alineen.
   */
  private worldOriginScreen: { x: number; y: number } = { x: 0, y: 0 };
  /**
   * Anillo bajo el sprite del player local que pulsa con la velocidad. Da
   * feedback al jugador del PC de qué cuadrado es "el suyo" y de que el
   * móvil está enviando intents (ADR-0007 onboarding del mando).
   */
  private localHeroRing: Phaser.GameObjects.Arc | null = null;
  private readonly snapshots = new SnapshotBuffer();
  private interpolationBufferMs = INTERPOLATION_BUFFER_MS_DEFAULT;
  private playerSprites = new Map<string, EntitySprite>();
  private enemySprites = new Map<string, EntitySprite>();
  private gameClient: GameClientHandle | null = null;
  private browserSessionToken: string | null = null;
  /** Última muestra rendereada por entityId — para reconciliación (TKT-018). */
  private lastRendered = new Map<string, EntitySample>();
  /** Hook expuesto para tests/observabilidad cliente. */
  onRendered?: (samples: EntitySample[]) => void;

  // HUD + capas (TKT-032).
  private hud: HudCombat | null = null;
  private decals: DecalLayer | null = null;
  private damageNumbers: DamageNumberLayer | null = null;
  private hitstop: HitstopController | null = null;
  private screenFlash: ScreenFlash | null = null;
  /**
   * HP/Ímpetu locales que muestra el HUD. El PC anfitrión es observador; el
   * Coro tiene 1 jugador en spike 6.5, así que pintamos el primer player vivo.
   * Cuando entre composición de Coro real, este campo se hará por seat.
   */
  private hudState: {
    playerId: string;
    playerHp: number;
    playerHpMax: number;
    playerMomentum: number;
    enemies: Map<string, { hp: number; hpMax: number; alias: string }>;
  } = {
    playerId: '',
    playerHp: 100,
    playerHpMax: 100,
    playerMomentum: 0,
    enemies: new Map(),
  };
  private unsubscribers: Array<() => void> = [];

  constructor() {
    super(GameScene.KEY);
  }

  init(data: { browserSessionToken?: string }): void {
    this.browserSessionToken = data?.browserSessionToken ?? null;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#0b0b0f');

    // Fondo cinemático placeholder (TKT futuro: optimizar y dejar definitivo).
    if (this.textures.exists('bg-game')) {
      const { width, height } = this.scale;
      const bg = this.add.image(width / 2, height / 2, 'bg-game');
      bg.setDisplaySize(width, height);
      bg.setDepth(-10);
      bg.setAlpha(0.35);
    }

    this.worldOriginScreen = { x: this.scale.width / 2, y: this.scale.height / 3 };
    drawIsoTilemap(this, { originScreen: this.worldOriginScreen });
    this.cameraPositionScreen = { x: this.scale.width / 2, y: this.scale.height / 2 };

    // HUD + capas (TKT-032).
    this.hud = new HudCombat();
    this.decals = new DecalLayer(this, DEFAULT_TILE);
    this.damageNumbers = new DamageNumberLayer(this, DEFAULT_TILE);
    this.hitstop = new HitstopController();
    this.screenFlash = new ScreenFlash();

    // Indicador del héroe local: anillo bajo el sprite (depth 0, debajo de
    // sprites). Invisible hasta que llega el primer player en snapshot.
    this.localHeroRing = this.add.circle(0, 0, 28, 0xf4ecd8, 0);
    this.localHeroRing.setStrokeStyle(2, 0xf4ecd8, 0.35);
    this.localHeroRing.setDepth(0);
    this.localHeroRing.setVisible(false);

    if (this.browserSessionToken) {
      void this.attachToServer(this.browserSessionToken);
    }
  }

  shutdown(): void {
    for (const u of this.unsubscribers) u();
    this.unsubscribers = [];
    void this.gameClient?.leave();
    this.gameClient = null;
    for (const sprite of this.playerSprites.values()) sprite.destroy();
    for (const sprite of this.enemySprites.values()) sprite.destroy();
    this.playerSprites.clear();
    this.enemySprites.clear();
    this.hud?.destroy();
    this.decals?.destroy();
    this.damageNumbers?.clear();
    this.screenFlash?.destroy();
    this.hud = null;
    this.decals = null;
    this.damageNumbers = null;
    this.screenFlash = null;
    this.hitstop?.clear();
    this.hitstop = null;
    this.localHeroRing?.destroy();
    this.localHeroRing = null;
  }

  setCameraTarget(world: CameraTarget | null): void {
    this.cameraTargetWorld = world;
  }

  setInterpolationBufferMs(ms: number): void {
    this.interpolationBufferMs = Math.max(50, Math.min(250, Math.floor(ms)));
  }

  /** Hook para tests: inyecta un snapshot sin pasar por WS. */
  injectSnapshotForTesting(snap: GameSnapshotPayload): void {
    this.snapshots.push(snap);
  }

  /** Devuelve la última muestra rendereada de una entidad (TKT-018). */
  getLastRenderedSample(entityId: string): EntitySample | undefined {
    return this.lastRendered.get(entityId);
  }

  /** Acceso al GameClient (consumido por HUD cableado en TKT-032). */
  getGameClient(): GameClientHandle | null {
    return this.gameClient;
  }

  private async attachToServer(browserSessionToken: string): Promise<void> {
    try {
      this.gameClient = await connectGameRoom({ browserSessionToken });
      const reconcile = createRoomReconciler(this.gameClient.room, (id) =>
        this.lastRendered.get(id),
      );
      this.unsubscribers.push(
        this.gameClient.onSnapshot((snap) => {
          this.snapshots.push(snap);
          reconcile(snap);
          this.syncHudFromSnapshot(snap);
        }),
      );
      this.unsubscribers.push(
        this.gameClient.onHpChanged((p) => {
          // Si el HP afectado pertenece al player local, screen flash + HUD update.
          if (this.hudState.playerId === '' || this.hudState.playerId === p.entityId) {
            this.hudState.playerId = p.entityId;
            this.hudState.playerHp = p.value;
            if (p.delta < 0) this.screenFlash?.trigger(Math.min(0.5, -p.delta / 50));
          }
          const enemy = this.hudState.enemies.get(p.entityId);
          if (enemy) enemy.hp = p.value;
          this.refreshHud();
        }),
      );
      this.unsubscribers.push(
        this.gameClient.onMomentumChanged((p) => {
          if (this.hudState.playerId === '' || this.hudState.playerId === p.playerSeatId) {
            this.hudState.playerId = p.playerSeatId;
            this.hudState.playerMomentum = p.value;
            this.refreshHud();
          }
        }),
      );
      this.unsubscribers.push(
        this.gameClient.onDamageResolved((p) => {
          if (p.amount > 0) {
            // Damage number en la posición del target.
            const targetSample =
              this.lastRendered.get(p.targetId) ??
              ({
                entityId: p.targetId,
                position: { x: 0, y: 0 },
                velocity: { x: 0, y: 0 },
              } as EntitySample);
            this.damageNumbers?.spawn(targetSample.position, p.amount, p.isCrit);
            // Hitstop visual al atacante (mitigación riesgo §spike-6:
            // sólo el sprite del atacante local).
            const attackerSample = this.lastRendered.get(p.attackerId);
            if (attackerSample) this.hitstop?.trigger(p.attackerId, attackerSample.position);
          }
        }),
      );
      this.unsubscribers.push(
        this.gameClient.onTelegraphStarted((p) => {
          this.decals?.add(p, Date.now());
        }),
      );
      this.unsubscribers.push(
        this.gameClient.onTelegraphResolved((p) => {
          this.decals?.remove(p.telegraphId);
        }),
      );
      this.unsubscribers.push(
        this.gameClient.onEntityRemoved((p) => {
          this.hudState.enemies.delete(p.entityId);
          this.refreshHud();
        }),
      );
      // Expone hooks de test (TKT-020/032).
      const expose = window as unknown as { __trames__?: Record<string, unknown> };
      expose.__trames__ = expose.__trames__ ?? {};
      expose.__trames__['gameClient'] = this.gameClient;
      expose.__trames__['hudState'] = this.hudState;
    } catch (err) {
      console.warn('[GameScene] connectGameRoom failed', err);
    }
  }

  /**
   * Lee el state del room para sembrar/actualizar el HUD. En spike 6.5 el
   * Coro es 1+1, así que tomamos el primer player vivo como referencia.
   */
  private syncHudFromSnapshot(_snap: GameSnapshotPayload): void {
    const state = this.gameClient?.room.state as
      | {
          players: Map<string, { id: string; hp: number; hpMax: number; momentum: number }>;
          enemies: Map<string, { id: string; hp: number; hpMax: number; alias: string }>;
        }
      | undefined;
    if (!state) return;
    const firstPlayer = state.players.values().next().value as
      | { id: string; hp: number; hpMax: number; momentum: number }
      | undefined;
    if (firstPlayer) {
      this.hudState.playerId = firstPlayer.id;
      this.hudState.playerHp = firstPlayer.hp;
      this.hudState.playerHpMax = firstPlayer.hpMax;
      this.hudState.playerMomentum = firstPlayer.momentum;
    }
    this.hudState.enemies.clear();
    for (const e of state.enemies.values()) {
      this.hudState.enemies.set(e.id, { hp: e.hp, hpMax: e.hpMax, alias: e.alias });
    }
    this.refreshHud();
  }

  private refreshHud(): void {
    this.hud?.update({
      playerHp: this.hudState.playerHp,
      playerHpMax: this.hudState.playerHpMax,
      playerMomentum: this.hudState.playerMomentum,
      enemies: this.hudState.enemies,
    });
  }

  override update(_time: number, delta: number): void {
    this.renderSnapshots();
    this.decals?.update();
    this.damageNumbers?.update();
    this.updateCamera(delta);
  }

  private renderSnapshots(): void {
    const newest = this.snapshots.newest();
    if (!newest) return;
    const targetTime = newest.serverTime - this.interpolationBufferMs;
    const pair = this.snapshots.getInterpolationPair(targetTime);

    let playerSamples: EntitySample[] = [];
    let enemySamples: EntitySample[] = [];
    if (pair) {
      playerSamples = interpolateEntities(pair.from.entities, pair.to.entities, pair.t);
      enemySamples = interpolateEntities(pair.from.enemies, pair.to.enemies, pair.t);
    } else {
      const stale = Date.now() - (newest.serverTime + this.interpolationBufferMs);
      if (stale <= SNAPSHOT_STALL_TIMEOUT_MS) {
        playerSamples = newest.entities.map((e) => ({
          entityId: e.entityId,
          position: e.position,
          velocity: e.velocity,
        }));
        enemySamples = newest.enemies.map((e) => ({
          entityId: e.entityId,
          position: e.position,
          velocity: e.velocity,
        }));
      } else {
        return;
      }
    }

    this.syncSpriteMap(playerSamples, this.playerSprites, 'player');
    this.syncSpriteMap(enemySamples, this.enemySprites, 'enemy');

    this.onRendered?.([...playerSamples, ...enemySamples]);
  }

  private syncSpriteMap(
    samples: EntitySample[],
    target: Map<string, EntitySprite>,
    kind: SpriteKind,
  ): void {
    const seen = new Set<string>();
    for (const s of samples) {
      seen.add(s.entityId);
      // Hitstop: si el sprite del atacante local está congelado, se queda en
      // su posición previa hasta que expire la ventana.
      const frozen = this.hitstop?.getFrozen(s.entityId);
      const position = frozen ?? s.position;
      const raw = worldToScreen(position, DEFAULT_TILE);
      // Aplica el origen del tilemap para que sprites y tiles compartan eje.
      const screen = {
        x: raw.x + this.worldOriginScreen.x,
        y: raw.y + this.worldOriginScreen.y,
      };
      let sprite = target.get(s.entityId);
      if (!sprite) {
        sprite = new EntitySprite(this, s.entityId, screen, kind);
        target.set(s.entityId, sprite);
      } else {
        sprite.setScreenPosition(screen);
      }
      // Posiciona el anillo del héroe local sobre el player del HUD.
      if (kind === 'player' && this.localHeroRing && s.entityId === this.hudState.playerId) {
        this.localHeroRing.setVisible(true);
        this.localHeroRing.setPosition(screen.x, screen.y + 6);
        const speed = Math.hypot(s.velocity.x, s.velocity.y);
        const moving = speed > 0.01;
        // Alpha base + boost mientras se mueve para "respirar" con el mando.
        const alpha = moving ? 0.85 : 0.35;
        this.localHeroRing.setStrokeStyle(moving ? 3 : 2, 0xf4ecd8, alpha);
      }
      this.lastRendered.set(s.entityId, s);
    }
    for (const [id, sprite] of target) {
      if (!seen.has(id)) {
        sprite.destroy();
        target.delete(id);
        this.lastRendered.delete(id);
      }
    }
  }

  private updateCamera(delta: number): void {
    if (!this.cameraTargetWorld) return;
    const targetScreen = worldToScreen(this.cameraTargetWorld, DEFAULT_TILE);
    const factor = Math.min(1, delta / 200);
    this.cameraPositionScreen = lerpCameraStep(
      this.cameraPositionScreen,
      { x: targetScreen.x, y: targetScreen.y },
      factor,
    );
    this.cameras.main.centerOn(this.cameraPositionScreen.x, this.cameraPositionScreen.y);
  }
}
